import {
    Container,
    Grid,
    Paper,
    Theme,
    Typography,
} from "@mui/material";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from "clsx";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IEvent } from "../../models/IEvent";
import CircuitServiceInstance from "../../services/CircuitService";
import EventList from "../events/EventList";
import SeasonRangeSelectorControl from "../common/SeasonRangeSelectorControl";
import OntimeBreadcrumb from "../common/OntimeBreadcrumb";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        appBarSpacer: theme.mixins.toolbar,
        fixedHeight: {
            maxHeight: 640,
            overflow: 'auto',
        },
    }));

function CircuitEventsPage() {
    const { circuitId } = useParams();
    const [liveAndPastEvents, setLiveAndPastEvents] = useState<IEvent[]>([]);
    const [upcomingEvents, setUpcomingEvents] = useState<IEvent[]>([]);
    const [circuitName, setCircuitName] = useState<string>('');
    const classes = useStyles();
    const { t } = useTranslation("common");

    useEffect(() => {
        if (!circuitId) return;

        const source = axios.CancelToken.source();
        CircuitServiceInstance.getCircuit(circuitId, source.token).then((circuit) => {
            setCircuitName(circuit.name);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.error('Failed to fetch circuit:', error);
            }
        });

        CircuitServiceInstance.getEvents(circuitId, undefined, undefined, source.token).then((events: IEvent[]) => {
            setLiveAndPastEvents(events?.filter(e => e.status > 0) ?? []);
            setUpcomingEvents(events?.filter(e => e.status === 0) ?? []);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.error('Failed to fetch circuit events:', error);
            }
        });

        return () => {
            source.cancel();
        };
    }, [circuitId]);

    const seasonRangeChanged = (event: Event, seasons: number | number[]) => {
        if (!circuitId) return;
        let start: Date, end: Date;
        if (typeof seasons === "number") {
            start = new Date(seasons, 0, 1);
            end = new Date(seasons, 11, 31);
        } else {
            start = new Date(seasons[0], 0, 1);
            end = new Date(seasons[1], 11, 31);
        }
        CircuitServiceInstance.getEvents(circuitId, start, end).then((events: IEvent[]) => {
            setLiveAndPastEvents(events?.filter(e => e.status > 0) ?? []);
            setUpcomingEvents(events?.filter(e => e.status === 0) ?? []);
        });
    };

    const fixedHeightPaper = clsx(classes.fixedHeight);

    const breadcrumbMap: Record<string, string> = {
        [t('mainList.circuits')]: '/circuits',
        [circuitName]: `/circuits/${circuitId}`,
    };

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={breadcrumbMap} current={t("circuit.events")} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h4">{t("circuit.events")}</Typography>
                        <SeasonRangeSelectorControl seasonRangeChanged={seasonRangeChanged} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper className={fixedHeightPaper}>
                            <Typography>{t("events.upcomingEvents")}</Typography>
                            <EventList events={upcomingEvents} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper className={fixedHeightPaper}>
                            <Typography>{t("events.pastEvents")}</Typography>
                            <EventList events={liveAndPastEvents} />
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </main>
    );
}

export default CircuitEventsPage;
